import {
  EmailAuthProvider, getMultiFactorResolver, multiFactor, reauthenticateWithCredential,
  reauthenticateWithPopup, GoogleAuthProvider, TotpMultiFactorGenerator,
} from "firebase/auth";
import { auth } from "./firebase";
import { recordAuthFailure } from "./authDiagnostics";

const failure = (error) => ({ error: error?.code === "auth/maximum-second-factor-count-exceeded" ? "limit" : ["auth/operation-not-allowed", "auth/unsupported-first-factor", "auth/invalid-multi-factor-session"].includes(error?.code) ? "unavailable" : "verify" });
export const getMfaFactors = () => auth.currentUser ? multiFactor(auth.currentUser).enrolledFactors.map(({ uid, displayName, factorId }) => ({ uid, displayName, factorId })) : [];
export const createMfaChallenge = (error) => {
  const resolver = getMultiFactorResolver(auth, error);
  return { resolver, factors: resolver.hints.filter((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID).map(({ uid, displayName, factorId }) => ({ uid, displayName, factorId })) };
};
export async function resolveTotpChallenge(challenge, factorUid, code) {
  if (!/^\d{6}$/.test(code) || !challenge?.factors.some((factor) => factor.uid === factorUid)) return { error: "code" };
  const startedAt = Date.now();
  try { return { ok: true, credential: await challenge.resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(factorUid, code)) }; }
  catch (error) {
    recordAuthFailure("totp", error, startedAt);
    const reason = ["auth/invalid-multi-factor-session", "auth/missing-multi-factor-session", "auth/session-expired"].includes(error?.code) ? "expired-session"
      : ["auth/firebase-app-check-token-is-invalid", "auth/invalid-app-credential", "auth/network-request-failed"].includes(error?.code) ? "unavailable" : "code";
    return { ...failure(error), reason };
  }
}
export async function reauthenticateAccount(password) {
  const user = auth.currentUser;
  if (!user) return { error: "verify" };
  try {
    if (password && user.providerData.some((provider) => provider.providerId === "password")) await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));
    else if (user.providerData.some((provider) => provider.providerId === "google.com")) await reauthenticateWithPopup(user, new GoogleAuthProvider());
    else return { error: "verify" };
    return { ok: true };
  } catch (error) {
    if (error.code === "auth/multi-factor-auth-required") return { mfa: createMfaChallenge(error) };
    return failure(error);
  }
}
export async function beginTotpEnrollment() {
  if (!auth.currentUser?.emailVerified) return { error: "verify" };
  if (getMfaFactors().some((factor) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID)) return { error: "limit" };
  if (auth.emulatorConfig) return { error: "unavailable" };
  try {
    const session = await multiFactor(auth.currentUser).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    return { ok: true, secret, uri: secret.generateQrCodeUrl(auth.currentUser.email, "AXORIS") };
  } catch (error) { return failure(error); }
}
export async function enrollTotp(secret, code, label) {
  if (!/^\d{6}$/.test(code) || !secret || !auth.currentUser) return { error: "code" };
  try { await multiFactor(auth.currentUser).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code), label); return { ok: true }; }
  catch (error) { return failure(error); }
}
export async function removeMfaFactor(uid) {
  try { await multiFactor(auth.currentUser).unenroll(uid); return { ok: true }; }
  catch (error) { return failure(error); }
}
